import { Component, type ErrorInfo, type ReactNode } from "react";
import { Alert, Button } from "../ds";

// Ohne Fangnetz macht ein einzelner Renderfehler die ganze Seite weiß — bei
// einem Werkzeug, das Router neu startet, ist das die schlechteste aller
// Rückmeldungen. Hier bleibt wenigstens sichtbar, WAS kaputt ist; der Build
// liefert Sourcemaps aus, der Stack ist also lesbar.

interface State {
  error: Error | null;
  stack: string | null;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null, stack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // In der Browser-Konsole mit vollem Stack, damit ein Screenshot reicht.
    console.error("Unbehandelter Fehler in der Oberfläche:", error, info.componentStack);
    this.setState({ stack: info.componentStack ?? null });
  }

  render(): ReactNode {
    const { error, stack } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="min-h-screen bg-app p-6 text-content">
        <div className="mx-auto flex max-w-3xl flex-col gap-4">
          <Alert variant="danger" title="Die Oberfläche ist auf einen Fehler gelaufen">
            Der Server ist davon nicht betroffen — laufende Builds und Router-Aktionen laufen
            weiter. Bitte den folgenden Text melden, dann lässt sich die Ursache eingrenzen.
          </Alert>
          <pre className="font-mono max-h-96 overflow-auto rounded-md bg-sunken p-3 text-xs text-content-2">
            {error.message}
            {"\n\n"}
            {error.stack}
            {stack ? `\n\nKomponenten:${stack}` : ""}
          </pre>
          <div>
            <Button iconName="refresh" onClick={() => window.location.reload()}>
              Neu laden
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
